import React from "react";
import { Badge } from "@/components/ui/badge";
import { ArrowDownRight, ArrowUpRight, ExternalLink, Minus, Sparkles } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Legend } from "recharts";
import { AnalysisSourceCard } from "@/components/AnalysisSourceCard";
import { buildGaReportUrl, gaReportLabel } from "@/lib/ga-deep-links";

// "View in GA4 →" subtle text link rendered under each actionable insight.
// Returns null when there's no GA property (e.g. CSV / demo analyses) so the
// card stays clean.
function ViewInGaLink({ propertyId, gaReport, period, testId }) {
  const href = buildGaReportUrl(propertyId, gaReport, period);
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="no-print inline-flex items-center gap-1 mt-2 text-xs font-medium text-[#1E392A]/55 hover:text-[#E27D60] transition-colors"
    >
      View in GA4 · {gaReportLabel(gaReport)}
      <ExternalLink className="w-3 h-3" aria-hidden="true" />
    </a>
  );
}

// Normalize wins/concerns which historically were plain strings but are now
// `{text, ga_report}` objects. Older saved analyses must still render cleanly.
function normalizeInsight(item) {
  if (typeof item === "string") return { text: item, ga_report: null };
  return { text: item?.text || "", ga_report: item?.ga_report || null };
}

const METRIC_LABELS = {
  sessions: "Sessions",
  users: "Users",
  pageviews: "Pageviews",
  conversions: "Conversions",
  bounce_rate: "Bounce rate",
  engagement_rate: "Engagement rate",
};
const PERCENT_METRICS = new Set(["bounce_rate", "engagement_rate"]);

function fmtVal(metric, v) {
  if (v === undefined || v === null) return "—";
  if (PERCENT_METRICS.has(metric)) return `${Number(v).toFixed(1)}%`;
  return Number(v).toLocaleString();
}

function ChangePill({ change, inverted = false }) {
  if (change === null || change === undefined) return null;
  const flat = Math.abs(change) < 0.5;
  const up = change > 0;
  const good = flat ? null : (inverted ? !up : up);
  const color = flat
    ? "bg-[#EAE7DD] text-[#1E392A]"
    : good
      ? "bg-[#1E392A] text-[#85CDCB]"
      : "bg-[#E27D60]/15 text-[#a8492a]";
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${color}`}>
      <Icon className="w-3 h-3" /> {Math.abs(change).toFixed(1)}%
    </span>
  );
}

function MetricCard({ metric, deltas }) {
  const d = deltas?.[metric];
  if (!d) return null;
  const inverted = metric === "bounce_rate";
  const isSnapshot = d.previous === null || d.previous === undefined;
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-5 bento-shadow" data-testid={`metric-card-${metric}`}>
      <div className="flex items-center justify-between">
        <div className="tag-label text-[#1E392A]/55">{METRIC_LABELS[metric]}</div>
        {!isSnapshot && <ChangePill change={d.change_pct} inverted={inverted} />}
      </div>
      <div className="font-display text-3xl mt-3">{fmtVal(metric, d.current)}</div>
      {!isSnapshot && (
        <div className="text-xs text-[#1E392A]/55 mt-1">vs {fmtVal(metric, d.previous)} prev</div>
      )}
    </div>
  );
}

function VerdictBadge({ verdict }) {
  const map = {
    up: { c: "bg-[#1E392A] text-[#85CDCB]", l: "Up" },
    down: { c: "bg-[#E27D60]/15 text-[#a8492a]", l: "Down" },
    flat: { c: "bg-[#EAE7DD] text-[#1E392A]", l: "Flat" },
    new: { c: "bg-[#85CDCB]/30 text-[#1E392A]", l: "New" },
  };
  const s = map[verdict] || map.flat;
  return <span className={`text-xs font-medium px-2 py-1 rounded-full ${s.c}`}>{s.l}</span>;
}

function OutlierKindBadge({ kind, accent }) {
  const map = {
    spike: { c: "bg-[#1E392A] text-[#85CDCB]", l: "Spike" },
    drop: { c: `text-white`, style: { background: accent }, l: "Drop" },
    new_or_resurfaced: { c: "bg-[#85CDCB]/30 text-[#1E392A]", l: "New" },
    vanished: { c: "bg-[#EAE7DD] text-[#1E392A]/60", l: "Gone" },
  };
  const s = map[kind] || map.spike;
  return (
    <span
      className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full whitespace-nowrap mt-0.5 shrink-0 ${s.c}`}
      style={s.style}
    >
      {s.l}
    </span>
  );
}

function ImpactBadge({ impact }) {
  const map = {
    high: "bg-[#E27D60] text-white",
    medium: "bg-[#D4A373] text-white",
    low: "bg-[#85CDCB] text-[#1E392A]",
  };
  return <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${map[impact] || map.low}`}>{impact}</span>;
}

function buildChartData(cur, prev) {
  const len = Math.max(cur?.length || 0, prev?.length || 0);
  const rows = [];
  for (let i = 0; i < len; i++) {
    rows.push({
      label: `Day ${i + 1}`,
      current_sessions: cur?.[i]?.sessions ?? null,
      previous_sessions: prev?.[i]?.sessions ?? null,
      current_users: cur?.[i]?.users ?? null,
      previous_users: prev?.[i]?.users ?? null,
    });
  }
  return rows;
}

export function BrandingHeader({ branding }) {
  if (!branding || (!branding.brand_company && !branding.brand_logo_url)) return null;
  const accent = branding.brand_color || "#E27D60";
  return (
    <div className="rounded-3xl bg-white border border-black/5 p-5 md:p-6 mb-6 bento-shadow flex items-center gap-4" data-testid="branding-header">
      {branding.brand_logo_url ? (
        <img src={branding.brand_logo_url} alt={branding.brand_company || "logo"} className="w-12 h-12 rounded-xl object-cover bg-[#F7F5F0]" />
      ) : (
        <div className="w-12 h-12 rounded-xl grid place-items-center text-white font-display text-xl" style={{ background: accent }}>
          {(branding.brand_company || "?").slice(0, 1)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-display text-xl truncate" style={{ color: accent }}>{branding.brand_company || "Custom report"}</div>
        {branding.brand_tagline && <div className="text-sm text-[#1E392A]/60 truncate">{branding.brand_tagline}</div>}
      </div>
    </div>
  );
}

export function AnalysisReport({ analysis, branding }) {
  const a = analysis;
  if (!a) return null;
  const ai = a.ai || {};
  const isSnapshot = a.is_snapshot || !a.previous_period || !a.metrics_previous;
  const chartData = buildChartData(a.timeseries_current, a.timeseries_previous);
  const accent = branding?.brand_color || "#E27D60";

  return (
    <article className="analysis-report">
      <BrandingHeader branding={branding} />

      <AnalysisSourceCard analysis={a} />

      <header className="mb-8">
        <div className="tag-label" style={{ color: accent }}>{isSnapshot ? "Snapshot" : "Analysis"}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-tight">{ai.headline || "Your analysis"}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-[#1E392A]/60">
          <Badge variant="outline" className="rounded-full">{a.property_label || "Untitled site"}</Badge>
          <span>·</span>
          <span>{a.current_period?.label}</span>
          {!isSnapshot && (
            <>
              <span>vs</span>
              <span>{a.previous_period?.label}</span>
            </>
          )}
          <span>·</span>
          <span>{new Date(a.created_at).toLocaleString()}</span>
        </div>
      </header>

      {/* Alert banner — only when something notable happened week-over-week */}
      {Array.isArray(a.alerts) && a.alerts.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="analysis-alerts">
          {a.alerts.slice(0, 3).map((al, i) => {
            const warning = al.severity === "warning";
            const dirWord = al.direction === "down" ? "down" : "up";
            return (
              <div
                key={i}
                className={`rounded-2xl p-4 flex items-center gap-3 border ${
                  warning
                    ? "bg-[#FFE9DF] border-[#E27D60]/30 text-[#a8492a]"
                    : "bg-[#E4F4EE] border-[#85CDCB]/40 text-[#1E392A]"
                }`}
                data-testid={`alert-${al.metric}`}
              >
                <span className="text-xl leading-none shrink-0">{al.emoji}</span>
                <div className="text-sm font-medium">
                  {al.label} {dirWord} {Math.abs(al.change_pct).toFixed(1)}% week-over-week
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary — split the AI's prose summary into sentence-level bullets at
          regular body size so it doesn't visually dominate the rest of the page. */}
      <div className="rounded-3xl bg-[#1E392A] text-[#F7F5F0] p-6 md:p-8 bento-shadow mb-8">
        <div className="flex items-center gap-2 text-[#85CDCB] mb-4">
          <Sparkles className="w-4 h-4" />
          <span className="text-[10px] uppercase tracking-wider font-semibold">Plain English summary</span>
        </div>
        {(() => {
          const sentences = (ai.summary || "")
            .split(/(?<=[.!?])\s+(?=[A-Z0-9"'(])/)
            .map((s) => s.trim())
            .filter(Boolean);
          if (sentences.length <= 1) {
            return <p className="text-base md:text-lg leading-relaxed text-[#F7F5F0]">{ai.summary}</p>;
          }
          return (
            <ul className="space-y-3" data-testid="ai-summary-bullets">
              {sentences.map((s, i) => (
                <li key={i} className="flex gap-3 text-base md:text-[17px] leading-relaxed text-[#F7F5F0]">
                  <span className="text-[#E27D60] mt-2.5 shrink-0" aria-hidden="true">
                    <span className="block w-2 h-2 rounded-full bg-[#E27D60]" />
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          );
        })()}
      </div>

      {/* Tier-2 context strip — YoY / Device / Pace. Only shown when the AI emitted them. */}
      {(ai.year_over_year || ai.device_insight || ai.month_pace) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-10" data-testid="tier2-context-strip">
          {ai.year_over_year && (
            <div className="rounded-2xl bg-white border border-black/5 p-5 bento-shadow" data-testid="yoy-card">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#1E392A]/70">Year over year</div>
                <VerdictBadge verdict={ai.year_over_year.verdict} />
              </div>
              <p className="text-[15px] text-[#1E392A] leading-relaxed font-medium">{ai.year_over_year.headline}</p>
            </div>
          )}
          {ai.month_pace && (
            <div className="rounded-2xl bg-white border border-black/5 p-5 bento-shadow" data-testid="month-pace-card">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#1E392A]/70 mb-2">On pace for</div>
              <p className="text-[15px] text-[#1E392A] leading-relaxed font-medium">{ai.month_pace.headline}</p>
            </div>
          )}
          {ai.device_insight && (
            <div className="rounded-2xl bg-white border border-black/5 p-5 bento-shadow" data-testid="device-card">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#1E392A]/70 mb-2">Mobile vs desktop</div>
              <p className="text-[15px] text-[#1E392A] leading-relaxed font-medium">{ai.device_insight.headline}</p>
              {ai.device_insight.recommendation && (
                <p className="mt-3 text-sm text-[#1E392A]/75 pt-3 border-t border-black/5">
                  <span className="font-semibold text-[#1E392A]">Next step:</span> {ai.device_insight.recommendation}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Metric grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
        {["sessions", "users", "pageviews", "conversions", "bounce_rate", "engagement_rate"].map((m) => (
          <MetricCard key={m} metric={m} deltas={a.deltas} />
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10 report-charts">
        <div className="rounded-3xl bg-white border border-black/5 p-6 bento-shadow">
          <div className="tag-label text-[#1E392A]/55 mb-3">Sessions over time</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAE7DD" />
              <XAxis dataKey="label" stroke="#8E8D8A" fontSize={11} />
              <YAxis stroke="#8E8D8A" fontSize={11} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EAE7DD" }} />
              <Legend />
              <Line type="monotone" dataKey="current_sessions" name="Current" stroke={accent} strokeWidth={2.5} dot={false} />
              {!isSnapshot && (
                <Line type="monotone" dataKey="previous_sessions" name="Previous" stroke="#1E392A" strokeWidth={2} strokeDasharray="4 4" dot={false} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-3xl bg-white border border-black/5 p-6 bento-shadow">
          <div className="tag-label text-[#1E392A]/55 mb-3">Users over time</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAE7DD" />
              <XAxis dataKey="label" stroke="#8E8D8A" fontSize={11} />
              <YAxis stroke="#8E8D8A" fontSize={11} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EAE7DD" }} />
              <Legend />
              {!isSnapshot && (
                <Bar dataKey="previous_users" name="Previous" fill="#85CDCB" radius={[6, 6, 0, 0]} />
              )}
              <Bar dataKey="current_users" name="Current" fill={accent} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Metric explanations */}
      {ai.metric_explanations?.length > 0 && (
        <section className="mb-10">
          <h2 className="font-display text-xl sm:text-2xl text-[#1E392A] mb-1">{isSnapshot ? "What each metric means" : "Why each metric moved"}</h2>
          <div className="h-0.5 w-12 bg-[#E27D60] rounded-full mb-5"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ai.metric_explanations.map((m, i) => (
              <div key={i} className="rounded-2xl bg-white border border-black/5 p-6 bento-shadow" data-testid={`explanation-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display text-xl text-[#1E392A]">{m.metric}</div>
                  <VerdictBadge verdict={m.verdict} />
                </div>
                <p className="text-[15px] text-[#1E392A] mt-3 leading-relaxed">{m.explanation}</p>
                {m.likely_reasons?.length > 0 && (
                  <ul className="mt-3 space-y-1.5 text-sm text-[#1E392A]/80 pt-3 border-t border-black/5">
                    {m.likely_reasons.map((r, j) => (
                      <li key={j} className="flex gap-2"><span style={{ color: accent }} className="font-bold">•</span>{r}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Channel insights — engagement & conversion per channel */}
      {ai.channel_insights?.length > 0 && (
        <section className="mb-10" data-testid="channel-insights-section">
          <h2 className="font-display text-xl sm:text-2xl text-[#1E392A] mb-1">What's driving traffic</h2>
          <div className="h-0.5 w-12 bg-[#E27D60] rounded-full mb-5"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ai.channel_insights.map((c, i) => (
              <div key={i} className="rounded-2xl bg-white border border-black/5 p-6 bento-shadow" data-testid={`channel-insight-${i}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="font-display text-xl text-[#1E392A]">{c.channel}</div>
                  <VerdictBadge verdict={c.verdict} />
                </div>
                <p className="text-[15px] text-[#1E392A] mt-3 leading-relaxed font-medium">{c.headline}</p>
                {c.recommendation && (
                  <p className="mt-4 text-sm text-[#1E392A]/80 pt-3 border-t border-black/5 leading-relaxed">
                    <span className="font-semibold text-[#1E392A]">Next step:</span> {c.recommendation}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Page-level outliers — pages that spiked, dropped, or vanished */}
      {ai.page_outliers?.length > 0 && (
        <section className="mb-10" data-testid="page-outliers-section">
          <h2 className="font-display text-xl sm:text-2xl text-[#1E392A] mb-1">Pages that broke the pattern</h2>
          <div className="h-0.5 w-12 bg-[#E27D60] rounded-full mb-5"></div>
          <ul className="space-y-3">
            {ai.page_outliers.map((p, i) => (
              <li key={i} className="rounded-2xl bg-white border border-black/5 p-5 bento-shadow flex gap-4 items-start" data-testid={`page-outlier-${i}`}>
                <OutlierKindBadge kind={p.kind} accent={accent} />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm text-[#1E392A]/80 break-all font-medium">{p.page}</div>
                  <p className="text-[15px] text-[#1E392A] mt-1.5 leading-relaxed font-medium">{p.headline}</p>
                  {p.likely_cause && (
                    <p className="mt-2 text-sm text-[#1E392A]/80 leading-relaxed">
                      <span className="font-semibold text-[#1E392A]">Likely cause:</span> {p.likely_cause}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Wins + Concerns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <div className="rounded-3xl bg-[#85CDCB]/15 border border-[#85CDCB]/40 p-6 bento-shadow">
          <h3 className="font-display text-lg text-[#1E392A] mb-1">Wins</h3>
          <div className="h-0.5 w-8 bg-[#85CDCB] rounded-full mb-4"></div>
          <ul className="space-y-3.5">
            {(ai.wins || []).map((raw, i) => {
              const w = normalizeInsight(raw);
              return (
                <li key={i} className="flex gap-3 text-[15px] text-[#1E392A] leading-relaxed" data-testid={`win-${i}`}>
                  <span className="text-[#1E392A] mt-0.5 font-bold text-base shrink-0">✓</span>
                  <div className="flex-1 min-w-0">
                    <span>{w.text}</span>
                    {w.ga_report && (
                      <div>
                        <ViewInGaLink
                          propertyId={a.ga_property_id}
                          gaReport={w.ga_report}
                          period={a.current_period}
                          testId={`win-${i}-ga-link`}
                        />
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="rounded-3xl bg-[#E27D60]/12 border border-[#E27D60]/40 p-6 bento-shadow">
          <h3 className="font-display text-lg text-[#1E392A] mb-1">Watch outs</h3>
          <div className="h-0.5 w-8 rounded-full mb-4" style={{ background: accent }}></div>
          <ul className="space-y-3.5">
            {(ai.concerns || []).map((raw, i) => {
              const c = normalizeInsight(raw);
              return (
                <li key={i} className="flex gap-3 text-[15px] text-[#1E392A] leading-relaxed" data-testid={`concern-${i}`}>
                  <span style={{ color: accent }} className="mt-0.5 font-bold text-base shrink-0">!</span>
                  <div className="flex-1 min-w-0">
                    <span>{c.text}</span>
                    {c.ga_report && (
                      <div>
                        <ViewInGaLink
                          propertyId={a.ga_property_id}
                          gaReport={c.ga_report}
                          period={a.current_period}
                          testId={`concern-${i}-ga-link`}
                        />
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Suggestions */}
      {ai.suggestions?.length > 0 && (
        <section className="mb-4">
          <h2 className="font-display text-xl sm:text-2xl text-[#1E392A] mb-1">What to try next</h2>
          <div className="h-0.5 w-12 bg-[#E27D60] rounded-full mb-5"></div>
          <ol className="space-y-3">
            {ai.suggestions.map((s, i) => (
              <li key={i} className="rounded-2xl bg-white border border-black/5 p-5 bento-shadow flex gap-4" data-testid={`suggestion-${i}`}>
                <div className="w-9 h-9 shrink-0 rounded-full text-[#F7F5F0] grid place-items-center font-display" style={{ background: "#1E392A" }}>{i + 1}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-lg text-[#1E392A]">{s.title}</div>
                    {s.impact && <ImpactBadge impact={s.impact} />}
                  </div>
                  <p className="text-[15px] text-[#1E392A] mt-1.5 leading-relaxed">{s.details}</p>
                  {s.ga_report && (
                    <ViewInGaLink
                      propertyId={a.ga_property_id}
                      gaReport={s.ga_report}
                      period={a.current_period}
                      testId={`suggestion-${i}-ga-link`}
                    />
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}
    </article>
  );
}
